import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronLeft, ChevronRight, Link2, X } from 'lucide-react';
import type { CompanySummary } from '../../../api/Navigation/moduleConfig';
import CompanyPicker from './CompanyPicker';

/**
 * Scope bar of the Module Configuration page (CO-864): which tenant the switches below belong to,
 * and the ways to move to another one. It stays at the top of the page scroller, so the tenant name
 * is on screen however far down the table the operator is.
 *
 * It never switches tenant by itself. Every control reports the company it wants through
 * `onRequestCompany`, and the page answers with the unsaved-changes prompt when there is staged work.
 */

export const COMPANY_PICKER_ID = 'module-config-company';
const COMPANY_LABEL_ID = 'module-config-company-label';

interface ScopeBarProps {
  /** The company list in the order the server returned it (by name). */
  companies: CompanySummary[];
  selectedId: number | null;
  /** `null` asks to leave the tenant and return to the picker alone. */
  onRequestCompany: (companyId: number | null) => void;
  /** True while a save is in flight: a save must not resolve against another tenant than the one on screen. */
  disabled: boolean;
}

const iconButtonClass =
  'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-card text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40';

const textButtonClass =
  'inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40';

export default function ScopeBar({ companies, selectedId, onRequestCompany, disabled }: ScopeBarProps) {
  const { t } = useTranslation();
  const [linkCopied, setLinkCopied] = useState(false);

  const index = companies.findIndex(company => company.companyId === selectedId);
  const previous = index > 0 ? companies[index - 1] : null;
  const next = index >= 0 && index < companies.length - 1 ? companies[index + 1] : null;

  // The confirmation is about the link of the tenant that was on screen; it does not follow a switch.
  useEffect(() => {
    setLinkCopied(false);
  }, [selectedId]);

  useEffect(() => {
    if (!linkCopied) return undefined;
    const timer = window.setTimeout(() => setLinkCopied(false), 2500);
    return () => window.clearTimeout(timer);
  }, [linkCopied]);

  const copyLink = async () => {
    if (selectedId === null) return;
    const url = `${window.location.origin}${window.location.pathname}?company=${selectedId}`;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
    } catch (error) {
      // Clipboard access can be refused (insecure origin, permission). The address bar already holds
      // the same link, so nothing is lost; the button simply does not confirm.
      console.error('Module Config: copying the tenant link failed', error);
    }
  };

  return (
    <div
      data-testid="module-config-scope-bar"
      className="sticky top-0 z-20 mb-6 rounded-lg border border-border bg-card px-3 py-2 shadow-sm"
    >
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
          <label id={COMPANY_LABEL_ID} htmlFor={COMPANY_PICKER_ID} className="shrink-0 text-sm font-medium text-foreground">
            {t('moduleConfig.picker.label')}
          </label>
          <div className="min-w-0 flex-1 lg:max-w-sm">
            <CompanyPicker
              id={COMPANY_PICKER_ID}
              labelId={COMPANY_LABEL_ID}
              companies={companies}
              selectedId={selectedId}
              onSelect={onRequestCompany}
              disabled={disabled}
            />
          </div>
        </div>

        {index >= 0 && (
          <div className="flex flex-wrap items-center gap-1.5 lg:ms-auto">
            <button
              type="button"
              className={iconButtonClass}
              aria-label={t('moduleConfig.picker.prev')}
              data-testid="module-config-company-prev"
              disabled={disabled || previous === null}
              onClick={() => previous && onRequestCompany(previous.companyId)}
            >
              <ChevronLeft aria-hidden="true" className="h-4 w-4 rtl:rotate-180" />
            </button>
            <span className="min-w-[3.5rem] text-center text-xs tabular-nums text-muted-foreground">
              {t('moduleConfig.picker.position', { current: index + 1, total: companies.length })}
            </span>
            <button
              type="button"
              className={iconButtonClass}
              aria-label={t('moduleConfig.picker.next')}
              data-testid="module-config-company-next"
              disabled={disabled || next === null}
              onClick={() => next && onRequestCompany(next.companyId)}
            >
              <ChevronRight aria-hidden="true" className="h-4 w-4 rtl:rotate-180" />
            </button>

            <button type="button" className={textButtonClass} data-testid="module-config-copy-link" onClick={copyLink}>
              {linkCopied ? (
                <Check aria-hidden="true" className="h-4 w-4 text-success-strong" />
              ) : (
                <Link2 aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
              )}
              {/* A live region, so the confirmation is announced and not only seen. */}
              <span role="status">{linkCopied ? t('moduleConfig.picker.linkCopied') : t('moduleConfig.picker.copyLink')}</span>
            </button>
            <button
              type="button"
              className={textButtonClass}
              data-testid="module-config-all-tenants"
              disabled={disabled}
              onClick={() => onRequestCompany(null)}
            >
              <X aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
              {t('moduleConfig.picker.allTenants')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
